import { createContext, useCallback, useContext, useMemo } from 'react'
import { findMembership, grants, type MembershipPermission } from '@/lib/access'
import { errorMessage } from '@/lib/errors'
import { getCompanyId } from '@/lib/session'
import { useMyAccess } from '@/hooks/useMyAccess'
import { useAuth } from '@/providers/auth'

interface PermissionContextValue {
  permissions: MembershipPermission[]
  loading: boolean
  error: string | null
  role: string
  subscriptionActive: boolean
  isSuperAdmin: boolean
  hasPermission: (resource: string, action?: string) => boolean
  refresh: () => Promise<void>
}

const PermissionsContext = createContext<PermissionContextValue>(null!)

export const usePermission = () => useContext(PermissionsContext)

const NO_PERMISSIONS: MembershipPermission[] = []

export function PermissionsProvider({ children, scopeCompanyId }: { children: React.ReactNode; scopeCompanyId?: string }) {
  const { company } = useAuth()
  const companyId = scopeCompanyId || company?.id || getCompanyId() || null
  const { data: access, isLoading, error, refetch } = useMyAccess()

  const membership = useMemo(
    () => (companyId ? access?.memberships.find(item => item.company.id === companyId) : findMembership(access)),
    [access, companyId]
  )
  const permissions = membership?.permissions ?? NO_PERMISSIONS
  const isSuperAdmin = !!access?.isSuperAdmin

  const hasPermission = useCallback(
    (resource: string, action?: string) => isSuperAdmin || grants(permissions, resource, action),
    [isSuperAdmin, permissions]
  )

  const refresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const value = useMemo<PermissionContextValue>(
    () => ({
      permissions,
      loading: isLoading && !access,
      error: error && !access ? errorMessage(error, 'Erro ao carregar permissões') : null,
      role: membership?.role ?? '',
      subscriptionActive: membership?.subscriptionActive ?? false,
      isSuperAdmin,
      hasPermission,
      refresh
    }),
    [permissions, isLoading, access, error, membership, isSuperAdmin, hasPermission, refresh]
  )

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}
